import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { batchesAPI, teacherModuleAPI } from '../../services/api';
import toast from 'react-hot-toast';

const toYMD = (d) => new Date(d).toISOString().slice(0, 10);
const trimTime = (t) => String(t || '').slice(0, 5);

function defaultEndTime(startTime, durationMinutes = 60) {
  const [h, m] = trimTime(startTime).split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return '10:00';
  const total = h * 60 + m + durationMinutes;
  const eh = Math.floor(total / 60) % 24;
  const em = total % 60;
  return `${String(eh).padStart(2, '0')}:${String(em).padStart(2, '0')}`;
};

export default function MakeupClassDialog({
  open,
  onClose,
  onSaved,
  batches = [],
  eligibleStudents = [],
  initialBatchId = '',
  initialStudentIds = [],
  editSession = null,
}) {
  const isEdit = !!editSession?.id;

  const [batchId, setBatchId] = useState('');
  const [sessionDate, setSessionDate] = useState(toYMD(new Date()));
  const [startTime, setStartTime] = useState('10:00');
  const [endTime, setEndTime] = useState('11:00');
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [notes, setNotes] = useState('');
  const [warnings, setWarnings] = useState([]);

  const batchStudentsQuery = useQuery(
    ['batchStudentsForMakeup', batchId],
    () => batchesAPI.getStudents(batchId),
    { enabled: !!batchId && open }
  );

  const studentOptions = useMemo(() => {
    const roster = batchStudentsQuery.data?.data?.data?.students ?? [];
    return roster.map((s) => ({
      id: s.student_id || s.id,
      label: s.student_name || `${s.first_name || ''} ${s.last_name || ''}`.trim() || 'Student',
    }));
  }, [batchStudentsQuery.data]);

  const eligibleForBatch = useMemo(
    () => eligibleStudents.filter((s) => String(s.batchId) === String(batchId)),
    [eligibleStudents, batchId]
  );

  useEffect(() => {
    if (!open) return;
    if (isEdit) {
      setBatchId(editSession.batch_id || '');
      setSessionDate(editSession.session_date || toYMD(new Date()));
      setStartTime(trimTime(editSession.start_time) || '10:00');
      setEndTime(trimTime(editSession.end_time) || '11:00');
      setNotes(editSession.notes || '');
      const scheduled = editSession.scheduled_students || editSession.students || [];
      setSelectedStudents(
        scheduled.map((s) => ({
          id: s.studentId || s.student_id,
          label: s.studentName || s.student_name || 'Student',
        }))
      );
      setWarnings([]);
      return;
    }
    setBatchId(initialBatchId || batches[0]?.id || '');
    setSessionDate(toYMD(new Date()));
    setStartTime('10:00');
    setEndTime('11:00');
    setNotes('');
    setWarnings([]);
    setSelectedStudents([]);
  }, [open, isEdit, editSession, initialBatchId, batches]);

  useEffect(() => {
    if (!open || isEdit || !batchId || initialStudentIds.length) return;
    if (eligibleForBatch.length > 0) {
      setSelectedStudents(
        eligibleForBatch.map((s) => ({
          id: s.studentId,
          label: `${s.studentName} · ${s.batchName}`,
        }))
      );
    }
  }, [open, isEdit, batchId, eligibleForBatch, initialStudentIds.length]);

  useEffect(() => {
    if (!open || isEdit || !initialStudentIds.length || !studentOptions.length) return;
    const picked = studentOptions.filter((o) => initialStudentIds.includes(o.id));
    if (picked.length) setSelectedStudents(picked);
  }, [open, isEdit, initialStudentIds, studentOptions]);

  const saveMutation = useMutation(
    async () => {
      const payload = {
        batchId,
        sessionDate,
        startTime,
        endTime,
        studentIds: selectedStudents.map((s) => s.id),
        notes,
      };
      if (isEdit) {
        const res = await teacherModuleAPI.updateMakeupClass(editSession.id, {
          sessionDate,
          startTime,
          endTime,
          studentIds: payload.studentIds,
          notes,
        });
        return res.data;
      }
      const res = await teacherModuleAPI.createMakeupClass(payload);
      return res.data;
    },
    {
      onSuccess: (body) => {
        const w = body?.data?.warnings || [];
        setWarnings(w);
        if (w.length) {
          w.forEach((msg) => toast(msg, { icon: '⚠️' }));
        }
        toast.success(isEdit ? 'Makeup class updated' : 'Makeup class scheduled');
        onSaved?.();
        onClose();
      },
      onError: (err) => {
        const w = err.response?.data?.warnings;
        if (Array.isArray(w) && w.length) setWarnings(w);
        toast.error(err.response?.data?.message || 'Failed to save makeup class');
      },
    }
  );

  const canSave = batchId && sessionDate && startTime && endTime && selectedStudents.length > 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? 'Edit makeup class' : 'Schedule makeup class'}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Agree date and time with the family offline, then record the makeup session here.
        </Typography>

        {warnings.length > 0 ? (
          <Stack spacing={1} sx={{ mb: 2 }}>
            {warnings.map((w) => (
              <Alert key={w} severity="warning">
                {w}
              </Alert>
            ))}
          </Stack>
        ) : null}

        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            select
            label="Batch"
            value={batchId}
            onChange={(e) => {
              setBatchId(e.target.value);
              setSelectedStudents([]);
            }}
            disabled={isEdit}
            fullWidth
            size="small"
            SelectProps={{ native: true }}
          >
            <option value="">Select batch</option>
            {batches.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </TextField>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <TextField
              label="Date"
              type="date"
              value={sessionDate}
              onChange={(e) => setSessionDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              size="small"
            />
            <TextField
              label="Start"
              type="time"
              value={startTime}
              onChange={(e) => {
                setStartTime(e.target.value);
                setEndTime(defaultEndTime(e.target.value));
              }}
              InputLabelProps={{ shrink: true }}
              fullWidth
              size="small"
            />
            <TextField
              label="End"
              type="time"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              size="small"
            />
          </Stack>

          <Autocomplete
            multiple
            options={studentOptions}
            value={selectedStudents}
            onChange={(_e, value) => setSelectedStudents(value)}
            getOptionLabel={(o) => o.label}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip {...getTagProps({ index })} key={option.id} label={option.label} size="small" />
              ))
            }
            renderInput={(params) => (
              <TextField {...params} label="Students" placeholder="Select students" size="small" />
            )}
            loading={batchStudentsQuery.isLoading}
          />

          {eligibleForBatch.length > 0 ? (
            <Box>
              <Typography variant="caption" color="text.secondary">
                Still need a makeup in this batch:
              </Typography>
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                {eligibleForBatch.map((s) => (
                  <Chip
                    key={`${s.studentId}-${s.batchId}`}
                    size="small"
                    variant="outlined"
                    color="warning"
                    label={`${s.studentName} (${Math.max(0, Number(s.makeupsLeft ?? s.classesAbsent) || 0)} to schedule)`}
                    onClick={() => {
                      const opt = studentOptions.find((o) => o.id === s.studentId);
                      if (!opt) return;
                      if (!selectedStudents.some((x) => x.id === opt.id)) {
                        setSelectedStudents((prev) => [...prev, opt]);
                      }
                    }}
                  />
                ))}
              </Stack>
            </Box>
          ) : null}

          <TextField
            label="Notes (optional)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            multiline
            minRows={2}
            fullWidth
            size="small"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!canSave || saveMutation.isLoading}
          onClick={() => saveMutation.mutate()}
        >
          {isEdit ? 'Save changes' : 'Schedule makeup'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
